import { KrogerLocation } from "./kroger";
import { MealPrepPreferences, ProductPreferences } from "./mealPlan";
import type { HouseholdSegment } from "../utils/householdSegment";

export type { ProductPreferences };

export type FlavorProfile = {
  flavorIntensity?: "mild" | "moderate" | "bold";
  preferences?: string; // Open-ended text for specific preferences
};

export type KrogerInfo = {
  location?: KrogerLocation;
  fulfillmentPreference?: "pickup" | "delivery";
};

export type CalendarPreferences = {
  enabled: boolean;
  mealEvents: boolean;
  prepReminders: boolean;
  shoppingReminders: boolean;
  defaultMealTime?: string;
  breakfastTime?: string; // HH:mm (default "07:30")
  lunchTime?: string; // HH:mm (default "12:00")
  reminderLeadTimes?: {
    thawMinutes: number;
    marinateMinutes: number;
  };
  shoppingReminderDaysBefore?: number;
  shoppingReminderTime?: string;
};

export type CookingStyle = {
  batchCooking?: boolean; // dinners default to batch (cook count derived per plan length)
  breakfastMakeAhead?: boolean;
  // Standing full-week rotation sizes (unique recipes covering the week).
  // Absent = the per-plan-length default curve; shorter plans clamp down.
  breakfastRotationSize?: number;
  lunchMode?: 'leftovers' | 'rotation';
  lunchMakeAhead?: boolean;
  lunchRotationSize?: number;
  prepWeekdays?: string[]; // "Sunday" etc.; the wizard maps these onto plan dates
};

// Every CookingStyle field is optional with a product default; readers must
// resolve through this so an absent field and an explicitly-default field are
// the same state (e.g. the unsaved-changes snapshot compares effective values).
// The rotation size defaults are the full-week (7-day) values of the backend's
// defaultRotationSize curve.
export const effectiveCookingStyle = (style?: CookingStyle): Required<CookingStyle> => ({
  batchCooking: style?.batchCooking ?? false,
  breakfastMakeAhead: style?.breakfastMakeAhead ?? true,
  breakfastRotationSize: style?.breakfastRotationSize ?? 3,
  lunchMode: style?.lunchMode ?? 'leftovers',
  lunchMakeAhead: style?.lunchMakeAhead ?? true,
  lunchRotationSize: style?.lunchRotationSize ?? 2,
  prepWeekdays: style?.prepWeekdays ?? [],
});

export type FamilyProfile = {
  adults: number;
  kids: number;
  dietaryRestrictions: string[];
  allergies: string[];
  cuisinePreferences: string[];
  avoidCuisines: string[];
  cuisineAdventure?: number;
  cookingSkill: "beginner" | "intermediate" | "advanced";
  dislikes: string[];
  favoriteProteins: string[];
  avoidProteins: string[];
  proteinDiversity?: "minimal" | "moderate" | "high";
  flavorProfile?: FlavorProfile;
  includeLeftovers: boolean;
  preferredCookingMethods?: string[]; // one_pot, set_forget, stovetop, grilling, air_fryer, baking, no_cook
  mealPrepPreferences?: MealPrepPreferences; // User's saved meal prep preferences
  // Standing cooking-style traits (additional_meals experiment): wizard
  // defaults come from these; any week can override in the wizard
  cookingStyle?: CookingStyle;
  planningMode?: "surprise_me" | "shuffle_cookbook" | "pick_a_few"; // Last-used wizard mode; pre-selects the mode step
  krogerConnected: boolean;
  krogerInfo?: KrogerInfo;
  pinterestConnected?: boolean;
  productPreferences?: ProductPreferences;
  googleCalendarConnected?: boolean;
  calendarPreferences?: CalendarPreferences;
  timezone?: string;
};

export type OnboardingStep = {
  id: string;
  title: string;
  description?: string;
  component: React.ComponentType<{
    value: any;
    onChange: (value: any) => void;
    onNext: () => void;
    onBack: () => void;
    isLastStep: boolean;
    isFirstStep?: boolean;
    isCompleting?: boolean;
    householdSegment?: HouseholdSegment;
  }>;
};
